import { useCallback, useMemo, useState } from 'react';
import { Box } from '@mui/material';

import { Button, SearchForm, TableContainer, TableHeader, snackbar } from '@occc-app/design-system';
import { Tab, Tabs } from '@occc/ui-design-system';
import {
  RetailerNetworkLocationFilter,
  RetailerNetworkLocationGroupCreateMutation,
  RetailerNetworkLocationGroupFilter,
} from '@occc/graphql';
import { useTabQueryParam } from '@occc/utils-query-params';
import { useBulkSelect } from '@occc-app/hooks/useBulkSelect';
import { Can } from '@occc-app/App/PolicyBasedAccessControl';
import {
  AddLocationsToRetailerNetwork,
  AddLocationsToRetailerNetworkLocationGroup,
  CreateRetailerNetworkLocationGroups,
} from '@occc-app/App/PolicyBasedAccessControl/userActions';
import { useSearchParams } from '@occc-app/hooks/useSearchParams';

import { LocationListContainer } from './retailer-network-locations/location-list.container';
import {
  RETAILER_NETWORK_LOCATIONS_AND_GROUPS_TAB_PARAM,
  DEFAULT_RETAILER_NETWORK_LOCATIONS_AND_GROUPS_TAB,
  RETAILER_NETWORK_LOCATIONS_AND_GROUPS_TABS,
  tabLabelsMap,
} from './retailer-network-locations-and-groups.constants';
import { RetailerNetworkLocationsAndGroupsTab } from './retailer-network-locations-and-groups.types';

import {
  useRetailerNetworkLocationGroupAddLocationsMutation,
  useRetailerNetworkLocationGroupCreateMutation,
} from '../../../../data-access/mutations';
import { LocationGroupFormValues } from '../../../components/location-group-update-dialog/location-group-update-dialog.types';
import { LocationGroupsTableContainer } from '../../../retailer-network-locations-configuration/retailer-network-location-groups/location-groups-table.container';
import { LocationGroupCreateDialog } from '../../../retailer-network-locations-configuration/retailer-network-location-groups/location-group-create-dialog.component';
import { AddLocationsToLocationGroupDialog } from '../../../retailer-network-locations-configuration/components/add-locations-to-location-group-dialog/add-locations-to-location-group-dialog.component';
import { AddLocationsToLocationGroupDialogFormValues } from '../../../retailer-network-locations-configuration/components/add-locations-to-location-group-dialog/add-locations-to-location-group-dialog.types';
import {
  LOCATIONS_PARAM_NAMES,
  LOCATION_GROUPS_PARAM_NAMES,
} from '../../../retailer-network-locations-configuration/retailer-network-locations-configuration.constants';
import { RetailerNetworkAddLocationsDialog } from '../../../retailer-network-locations-configuration/retailer-network-locations/retailer-network-add-locations-dialog.component';
import CreateLocationGroupAndAddLocationsDialog from '../../../retailer-network-locations-configuration/retailer-network-location-groups/create-location-group-and-add-locations-dialog/create-location-group-and-add-locations-dialog.container';
import { RetailerNetworkUpdateHandler } from '../../../retailer-connect.types';

const transitionStyle = 'transform 500ms cubic-bezier(.75, 0, .25, 1)';

const targetingBoxStyles = {
  transition: transitionStyle,
  overflow: 'hidden',
  width: '100%',
};

const commonStyles = (isActive: boolean) => ({
  transition: transitionStyle,
  width: '100%',
  overflow: 'hidden',
  maxHeight: isActive ? '100%' : '0px',
});

const locationsTabStyles = (isActive: boolean) => ({
  ...commonStyles(isActive),
  transform: isActive ? 'translate(0, 0)' : 'translate(-100%, 0)',
});

const locationGroupsTabStyles = (isActive: boolean) => ({
  ...commonStyles(isActive),
  transform: isActive ? 'translate(0, 0)' : 'translate(100%, 0)',
});

type RetailerNetworkLocationsAndGroupsProps = {
  retailerNetworkUuid: string;
  retailerNetworkUpdate: RetailerNetworkUpdateHandler;
  customerId: string;
};

export const RetailerNetworkLocationsAndGroups = ({
  retailerNetworkUuid,
  retailerNetworkUpdate,
  customerId,
}: RetailerNetworkLocationsAndGroupsProps) => {
  const [activeTab, setActiveTab] = useTabQueryParam<RetailerNetworkLocationsAndGroupsTab>(
    RETAILER_NETWORK_LOCATIONS_AND_GROUPS_TAB_PARAM,
    DEFAULT_RETAILER_NETWORK_LOCATIONS_AND_GROUPS_TAB,
    RETAILER_NETWORK_LOCATIONS_AND_GROUPS_TABS,
  );
  const [addLocationsToLocationGroupDialogOpen, setAddLocationsToLocationGroupDialogOpen] =
    useState<boolean>(false);
  const [addLocationsDialogOpen, setAddLocationsDialogOpen] = useState<boolean>(false);
  const [createLocationGroupDialogOpen, setCreateLocationGroupDialogOpen] =
    useState<boolean>(false);
  const [addLocationGroupDialogOpen, setAddLocationGroupDialogOpen] = useState<boolean>(false);

  const { bulkSelectedItems, handleBulkSelectMultiple, handleBulkDeselectAll, handleItemSelect } =
    useBulkSelect();

  const handleTabChange = (
    _event: React.SyntheticEvent<Element, Event>,
    value: RetailerNetworkLocationsAndGroupsTab,
  ) => {
    setActiveTab(value);
  };

  const handleOpenAddLocationsToLocationGroupDialog = () => {
    setAddLocationsToLocationGroupDialogOpen(!addLocationsToLocationGroupDialogOpen);
  };

  const handleOpenCreateLocationGroupDialog = () => {
    setCreateLocationGroupDialogOpen(!createLocationGroupDialogOpen);
  };

  const onLocationGroupCreateCompleted = async (
    data: RetailerNetworkLocationGroupCreateMutation,
  ) => {
    snackbar.success(
      `Locations were added to ${data.retailerNetworkLocationGroupCreate.name} location group`,
    );
    handleBulkDeselectAll();
    handleOpenCreateLocationGroupDialog();
  };

  const onLocationGroupCreateError = () => {
    snackbar.error('Unable to create Location Group. Please try a different name.');
  };

  const onAddLocationsToLocationGroupCompleted = () => {
    snackbar.success('Locations were added to Location Group.');
    handleBulkDeselectAll();
    handleOpenAddLocationsToLocationGroupDialog();
  };

  const onAddLocationsToLocationGroupError = () => {
    snackbar.error('Unable to update Location Group.');
  };

  const [createLocationGroup, { loading: locationGroupCreateLoading }] =
    useRetailerNetworkLocationGroupCreateMutation({
      onCompleted: onLocationGroupCreateCompleted,
      onError: onLocationGroupCreateError,
      refetchQueries: ['retailerNetworkLocationGroups'],
    });

  const [addLocationsToLocationGroup, { loading: addLocationsToLocationGroupLoading }] =
    useRetailerNetworkLocationGroupAddLocationsMutation({
      onCompleted: onAddLocationsToLocationGroupCompleted,
      onError: onAddLocationsToLocationGroupError,
      refetchQueries: ['retailerNetworkLocationGroups'],
    });

  const onLocationGroupCreateDialogFormSubmit = useCallback(
    async (values: LocationGroupFormValues) => {
      const locationUuids = Array.from(bulkSelectedItems);

      await createLocationGroup({
        variables: {
          retailerNetworkUuid,
          locationGroup: {
            name: values.name,
            locationUuids,
          },
        },
      });
    },
    [createLocationGroup, bulkSelectedItems, retailerNetworkUuid],
  );

  const onAddLocationsToLocationGroup = useCallback(
    async (values: AddLocationsToLocationGroupDialogFormValues) => {
      if (values.locationGroup) {
        const locationUuids = Array.from(bulkSelectedItems);
        await addLocationsToLocationGroup({
          variables: {
            retailerNetworkUuid,
            locationGroupUuid: values.locationGroup.uuid,
            locationUuids,
          },
        });
      }
    },
    [addLocationsToLocationGroup, bulkSelectedItems, retailerNetworkUuid],
  );

  const [locationsSearch, setLocationsSearch] = useSearchParams(LOCATIONS_PARAM_NAMES);
  const [locationGroupsSearch, setLocationGroupsSearch] = useSearchParams(
    LOCATION_GROUPS_PARAM_NAMES,
  );

  const locationsTabFilter: RetailerNetworkLocationFilter = {
    retailerNetworkUuidEquals: retailerNetworkUuid,
    search: locationsSearch,
  };

  const locationGroupsTabFilter: RetailerNetworkLocationGroupFilter = {
    retailerNetworkUuidEquals: retailerNetworkUuid,
    search: locationGroupsSearch,
  };

  const tableActions = useMemo(() => {
    if (activeTab === 'groups') {
      return (
        <Box display='flex'>
          <SearchForm
            onSubmit={({ search }) => setLocationGroupsSearch(search)}
            initialValues={{ search: locationGroupsSearch }}
          />
          <Can I={CreateRetailerNetworkLocationGroups({ uuid: retailerNetworkUuid })}>
            <Button
              variant='contained'
              sx={{ minWidth: 'fit-content', marginLeft: 2 }}
              onClick={() => setAddLocationGroupDialogOpen(true)}
              role='button'
            >
              Add group
            </Button>
          </Can>
        </Box>
      );
    }

    return (
      <Box display='flex'>
        <SearchForm
          onSubmit={({ search }) => setLocationsSearch(search)}
          initialValues={{ search: locationsSearch }}
        />
        <Can I={AddLocationsToRetailerNetwork({ uuid: retailerNetworkUuid })}>
          <Button
            variant='contained'
            sx={{ minWidth: 'fit-content', marginLeft: 2 }}
            onClick={() => setAddLocationsDialogOpen(true)}
            role='button'
          >
            Add locations
          </Button>
        </Can>
      </Box>
    );
  }, [
    locationsSearch,
    setLocationsSearch,
    locationGroupsSearch,
    setLocationGroupsSearch,
    activeTab,
    retailerNetworkUuid,
  ]);

  return (
    <>
      <TableContainer sx={{ mb: 1 }}>
        <TableHeader
          disableTitleTypography
          tabs={
            <Tabs
              variant='scrollable'
              scrollButtons='auto'
              value={activeTab}
              onChange={handleTabChange}
            >
              <Tab
                value={'locations' satisfies RetailerNetworkLocationsAndGroupsTab}
                label={tabLabelsMap.locations}
              />
              <Tab
                value={'groups' satisfies RetailerNetworkLocationsAndGroupsTab}
                label={tabLabelsMap.groups}
              />
            </Tabs>
          }
          actions={tableActions}
          bulkSelected={bulkSelectedItems.size}
          bulkActions={
            <Can I={AddLocationsToRetailerNetworkLocationGroup({ uuid: retailerNetworkUuid })}>
              <Button
                color='primary'
                variant='contained'
                onClick={handleOpenAddLocationsToLocationGroupDialog}
              >
                ADD TO GROUP
              </Button>
            </Can>
          }
        />
        <Box style={targetingBoxStyles}>
          <Box
            id={'locations' satisfies RetailerNetworkLocationsAndGroupsTab}
            style={locationsTabStyles(activeTab === 'locations')}
          >
            <LocationListContainer
              filter={locationsTabFilter}
              bulkSelectedItems={bulkSelectedItems}
              handleBulkSelectMultiple={handleBulkSelectMultiple}
              handleBulkDeselectAll={handleBulkDeselectAll}
              handleItemSelect={handleItemSelect}
              handleOpenAddLocationsToLocationGroupDialog={
                handleOpenAddLocationsToLocationGroupDialog
              }
              retailerNetworkUuid={retailerNetworkUuid}
            />
          </Box>
          <Box
            id={'groups' satisfies RetailerNetworkLocationsAndGroupsTab}
            style={locationGroupsTabStyles(activeTab === 'groups')}
          >
            <LocationGroupsTableContainer
              retailerNetworkUuid={retailerNetworkUuid}
              filter={locationGroupsTabFilter}
            />
          </Box>
        </Box>
      </TableContainer>

      {createLocationGroupDialogOpen && (
        <LocationGroupCreateDialog
          open={createLocationGroupDialogOpen}
          onClose={handleOpenCreateLocationGroupDialog}
          onSubmit={onLocationGroupCreateDialogFormSubmit}
          loading={locationGroupCreateLoading}
        />
      )}
      {addLocationsToLocationGroupDialogOpen && (
        <AddLocationsToLocationGroupDialog
          open={addLocationsToLocationGroupDialogOpen}
          onClose={handleOpenAddLocationsToLocationGroupDialog}
          onCreateNewGroupClick={handleOpenCreateLocationGroupDialog}
          onSubmit={onAddLocationsToLocationGroup}
          loading={addLocationsToLocationGroupLoading}
          bulkSelectedItems={bulkSelectedItems.size}
          retailerNetworkUuid={retailerNetworkUuid}
        />
      )}
      {addLocationsDialogOpen && (
        <RetailerNetworkAddLocationsDialog
          open={addLocationsDialogOpen}
          setDialogOpen={setAddLocationsDialogOpen}
          retailerNetworkUpdate={retailerNetworkUpdate}
          customerId={customerId}
          retailerNetworkUuid={retailerNetworkUuid}
        />
      )}
      {addLocationGroupDialogOpen && (
        <CreateLocationGroupAndAddLocationsDialog
          retailerNetworkUuid={retailerNetworkUuid}
          open={addLocationGroupDialogOpen}
          onClose={() => setAddLocationGroupDialogOpen(false)}
        />
      )}
    </>
  );
};
